
/**
 * Module dependencies.
 */

import { Buffer } from 'buffer';
import { SyntheticEvent, useContext } from 'react';
import { color } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { toHex } from './styles/colors';
import Switch from 'react-switch';
import checkIcon from './assets/svg/12/check.svg';
import styled, { ThemeContext, css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  'aria-label'?: string;
  checked: boolean;
  className?: string;
  disabled?: boolean;
  name?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onToggle: (checked: boolean, event: SyntheticEvent<MouseEvent | KeyboardEvent> | MouseEvent) => void;
};

/**
 * Toggle sizes.
 */

const toggleSizes = {
  height: 24,
  width: 40
};

/**
 * Get check icon.
 */

function getCheckIcon(disabled: boolean): string {
  const iconColor = disabled ? color('gray400') : color('blue500');
  const buffer = Buffer.from(checkIcon.replace('currentColor', iconColor));

  return buffer.toString('base64');
}

/**
 * `StyledSwitch` styled component.
 */

const StyledSwitch = styled(Switch)<{ checked: boolean; disabled?: boolean }>`
  &:focus-visible {
    border: 2px solid ${color('blue400')};
    border-radius: 22px !important;
  }

  .react-switch-bg {
    &:hover {
      ${ifProp('checked', css`
        background: ${color('blue600')} !important;
      `, css`
        background: ${color('gray600')} !important;
      `)}
    }

    ${ifProp('disabled', css`
      background: ${color('gray400')} !important;
    `)}
  }

  .react-switch-handle {
    display: grid !important;
    place-content: center;
  }

  .react-switch-handle::before {
    background-image: url(data:image/svg+xml;base64,${props => getCheckIcon(props?.disabled ?? false)});
    display: block;
    height: 12px;
    opacity: 0;
    transition: opacity ${theme('animations.fastTransition')};
    width: 12px;
  }

  ${ifProp('checked', css`
    .react-switch-handle::before {
      opacity: 1;
    }
  `)}
`;

/**
 * Export `BaseToggle` component.
 */

export function BaseToggle(props: Props): JSX.Element {
  const { onToggle, ...rest } = props;
  const themeContext = useContext(ThemeContext);

  return (
    <StyledSwitch
      {...rest}
      activeBoxShadow={theme('boxShadow.toggle')({ theme: themeContext ?? {} })}
      checkedIcon={false}
      handleDiameter={toggleSizes.height - 2}
      height={toggleSizes.height}
      offColor={toHex(color('gray500')({ theme: themeContext }))}
      offHandleColor={toHex(color('white')({ theme: themeContext }))}
      onChange={onToggle}
      onColor={toHex(color('primary')({ theme: themeContext }))}
      onHandleColor={toHex(color('white')({ theme: themeContext }))}
      uncheckedIcon={false}
      width={toggleSizes.width}
    />
  );
}
